import { useEffect, useState, useSyncExternalStore } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Shimmer,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  ariaKeyshortcuts,
  cn,
  pressIs,
  shortcutGlyph,
  shortcutPlatform,
  useSidebar,
} from '@tutero/ui';

import { viewAsUrl } from '@/lib/accounts/account-platform';
import { useStaff } from '@/shell/session';

import { kindsFor, type QuickSearchKind } from './quick-search';

/**
 * Quick Search's two halves (`quick-search.ts`): the sidebar's row, which opens the box, and the
 * box itself, mounted once by the shell so ⌘K / Ctrl+K opens it from every page. MathGaps/schools
 * draws its own the same way (`apps/web/src/shell/SearchPalette.tsx`): an open flag outside React
 * that the row and the key both set, the key heard in the capture phase, and the published
 * `CommandDialog` with cmdk's own filter off, since the service has already matched.
 */

/** ⌘K on a Mac, Ctrl+K elsewhere. */
const SHORTCUT = '$mod+k';

/** ⌘↵ on a Mac, Ctrl+Enter elsewhere: the highlighted hit's platform, as them, not its details. */
const PLATFORM = '$mod+enter';

/** How long typing rests before the box searches. */
const SETTLE = 250;

/* Open or closed, outside React: the row and the shortcut both set it. */
let open = false;
const listeners = new Set<() => void>();

function setOpen(next: boolean): void {
  if (next === open) return;
  open = next;
  for (const listener of listeners) listener();
}

function useOpen(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => open,
    () => open,
  );
}

/**
 * The sidebar's row, above the tools: a search box in look, a button in fact. Collapsed to the
 * rail it is the glyph alone, its tooltip naming the shortcut.
 */
export function QuickSearchButton() {
  const staff = useStaff();
  const { isMobile, setOpenMobile } = useSidebar();
  if (kindsFor(staff.perms).length === 0) return null;
  const platform = shortcutPlatform();
  const glyph = shortcutGlyph(SHORTCUT, platform);
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={{ children: 'Search', shortcut: glyph }}
          aria-haspopup="dialog"
          aria-keyshortcuts={ariaKeyshortcuts(SHORTCUT, platform)}
          onClick={() => {
            /* The phone's nav is a sheet over the page: the box opens in its place. */
            if (isMobile) setOpenMobile(false);
            setOpen(true);
          }}
          className="border-sidebar-border text-chrome-regular-m text-sidebar-foreground/70 hover:text-sidebar-foreground h-8 gap-2.5 border pr-1.5 pl-2.5 group-data-[collapsible=icon]:border-0"
        >
          <Icon name="search" size={18} />
          <span>Search</span>
          <kbd className="border-sidebar-border text-sidebar-foreground/70 ml-auto rounded-[4px] border px-1.5 font-sans text-[11px] leading-[18px] group-data-[collapsible=icon]:hidden">
            {glyph}
          </kbd>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** The box and its shortcut, for a staff member holding any kind it searches. */
export function QuickSearchHost() {
  const staff = useStaff();
  const kinds = kindsFor(staff.perms);
  const isOpen = useOpen();
  const { state, isMobile } = useSidebar();
  /* The highlighted hit's value (`<tool>:<id>`), so ⌘↵ knows whose platform to open. */
  const [highlighted, setHighlighted] = useState('');
  const searchable = kinds.length > 0;

  useEffect(() => {
    if (!searchable) return;
    /* In the capture phase, so the box answers before a page's own key handler takes the key. */
    const onKey = (event: KeyboardEvent) => {
      if (!pressIs(event, SHORTCUT)) return;
      event.preventDefault();
      event.stopPropagation();
      if (!event.repeat) setOpen(!open);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true });
      /* The flag outlives the host: a shell drawn again (a sign-in) starts closed. */
      setOpen(false);
    };
  }, [searchable]);

  if (!searchable) return null;
  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={setOpen}
      title="Search"
      description={`Search ${kinds.map((kind) => kind.title.toLowerCase()).join(', ')}`}
      showCloseButton={false}
      shouldFilter={false}
      value={highlighted}
      onValueChange={setHighlighted}
      className={cn('top-[40%] max-w-[640px]', pageCentre(state, isMobile))}
    >
      {/* Mounted only while open, so every opening starts from an empty box. */}
      {isOpen ? (
        <SearchBody
          kinds={kinds}
          highlighted={highlighted}
          close={() => setOpen(false)}
        />
      ) : null}
    </CommandDialog>
  );
}

/**
 * The box sits over the page, not the window: half the nav's width right of the window's middle.
 * The dialog is portalled out of the sidebar's provider, so it cannot read `--sidebar-width`;
 * the widths are `AppSidebar`'s 260 and the component's 3rem rail. A phone's nav is a sheet.
 */
function pageCentre(state: 'expanded' | 'collapsed', isMobile: boolean) {
  if (isMobile) return undefined;
  return state === 'expanded'
    ? 'left-[calc(50%+130px)]'
    : 'left-[calc(50%+1.5rem)]';
}

function SearchBody({
  kinds,
  highlighted,
  close,
}: {
  kinds: readonly QuickSearchKind[];
  highlighted: string;
  close: () => void;
}) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const typed = text.trim();
  const settled = useSettled(typed);
  const results = useQueries({
    queries: kinds.map((kind) => ({
      queryKey: ['quick-search', kind.tool, settled] as const,
      queryFn: () => kind.search(settled),
      enabled: settled !== '',
      staleTime: 30_000,
    })),
  });
  /* Still typing, or a kind still on its way: the rows are not yet the answer. */
  const waiting =
    typed !== settled || results.some((result) => result.isFetching);
  const found = kinds
    .map((kind, at) => ({ kind, hits: results[at]?.data ?? [] }))
    .filter(({ hits }) => hits.length > 0);

  const go = (kind: QuickSearchKind, id: string) => {
    close();
    void navigate({ to: '/$tool/$', params: { tool: kind.tool, _splat: id } });
  };
  /* The platform opens in a new tab, so the box stays as it was for the next one. */
  const platform = (kind: QuickSearchKind, id: string) =>
    window.open(viewAsUrl(id, kind.role), '_blank');
  const platformGlyph = shortcutGlyph(PLATFORM, shortcutPlatform());

  return (
    <>
      <CommandInput
        value={text}
        onValueChange={setText}
        onKeyDown={(event) => {
          if (!pressIs(event.nativeEvent, PLATFORM)) return;
          const hit = found
            .flatMap(({ kind, hits }) =>
              hits.map((each) => ({
                value: `${kind.tool}:${each.id}`,
                kind,
                each,
              })),
            )
            .find(({ value }) => value === highlighted);
          if (!hit) return;
          event.preventDefault();
          platform(hit.kind, hit.each.id);
        }}
        placeholder={`Search ${kinds.map((kind) => kind.title.toLowerCase()).join(', ')}`}
      />
      <CommandList className="max-h-[min(480px,60vh)]">
        {typed === '' ? (
          <p className="text-regular-s text-muted-foreground py-6 text-center">
            Search by name or email
          </p>
        ) : waiting && found.length === 0 ? (
          <Searching />
        ) : (
          <CommandEmpty>{`No results for “${typed}”`}</CommandEmpty>
        )}
        {found.map(({ kind, hits }) => (
          <CommandGroup key={kind.tool} heading={kind.title}>
            {hits.map((hit) => (
              <CommandItem
                key={hit.id}
                value={`${kind.tool}:${hit.id}`}
                onSelect={() => go(kind, hit.id)}
                className="group/hit gap-2.5"
              >
                <Icon name={kind.icon} set="classic" size={18} />
                <span className="truncate">{hit.name}</span>
                {hit.hint ? (
                  <span className="text-muted-foreground ml-auto shrink-0 truncate pl-4 text-xs">
                    {hit.hint}
                  </span>
                ) : null}
                <button
                  type="button"
                  aria-label={`Access Platform as ${hit.name}`}
                  title={`Access Platform (${platformGlyph})`}
                  /* Its own press, not the row's: the row opens the details. */
                  onClick={(event) => {
                    event.stopPropagation();
                    platform(kind, hit.id);
                  }}
                  className={cn(
                    'text-muted-foreground hover:bg-background hover:text-foreground flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md opacity-0 group-hover/hit:opacity-100 group-data-[selected=true]/hit:opacity-100 focus-visible:opacity-100',
                    !hit.hint && 'ml-auto',
                  )}
                >
                  <Icon name="visibility" size={16} />
                </button>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
      {found.length > 0 ? (
        <div className="text-muted-foreground flex items-center justify-end gap-4 border-t px-3 py-2 text-xs">
          <span className="flex items-center gap-1.5">
            <Kbd>↵</Kbd> Details
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>{platformGlyph}</Kbd> Access Platform
          </span>
        </div>
      ) : null}
    </>
  );
}

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="rounded-[4px] border px-1.5 font-sans text-[11px] leading-[18px]">
      {children}
    </kbd>
  );
}

/** Rows of shimmer where the hits will be, while the first answer is on its way. */
function Searching() {
  return (
    <div role="status" aria-label="Searching" className="p-1">
      {[0.55, 0.4, 0.62].map((width) => (
        <div key={width} className="flex items-center gap-2.5 px-2 py-2">
          <Shimmer className="size-[18px] shrink-0 rounded-[4px]" />
          <Shimmer
            className="h-3.5 rounded-[4px]"
            style={{ width: `${width * 100}%` }}
          />
        </div>
      ))}
    </div>
  );
}

/** `value` once it has stood still for `SETTLE`. */
function useSettled(value: string): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), SETTLE);
    return () => clearTimeout(id);
  }, [value]);
  return settled;
}
